import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { startSOADrill, submitSOAAnswer, completeSOADrill } from '../api/soa';
import { buildSOAContext } from '../data/mock_knowledgepacks';

/**
 * EMZI NEXUS V6.3 Phase 1A - SOA Drill In-Studio Component
 * 
 * Features:
 * - Adaptive coaching (repeat until score ≥8 or 3 attempts max)
 * - Knowledge Pack integration
 * - Bilingual support (BM/EN)
 * - Real-time per-domain scores
 * - Domain icons
 * - Nexus Academy prompt
 * 
 * Mobile-first: 390×844, EMZI CI (Blue/White/Orange)
 */

const SOADrillInStudio = ({ 
  onComplete, 
  onSkip, 
  productId = null, 
  campaignId = null,
  studioType = "content" 
}) => {
  const { user } = useAuth();
  
  // Domain configuration with icons
  const DOMAINS = [
    { name: "Problem", icon: "🎯", label_en: "Problem Awareness", label_bm: "Kesedaran Masalah" },
    { name: "Solution", icon: "💡", label_en: "Solution Understanding", label_bm: "Kefahaman Penyelesaian" },
    { name: "Offer", icon: "💬", label_en: "Offer Value", label_bm: "Nilai Tawaran" },
    { name: "Emotion", icon: "❤️", label_en: "Emotional Connection", label_bm: "Hubungan Emosi" },
    { name: "CTA", icon: "🔔", label_en: "Call To Action", label_bm: "Seruan Bertindak" }
  ];

  // State management
  const [step, setStep] = useState('intro'); // intro, drilling, results
  const [sessionId, setSessionId] = useState(null);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [domainScores, setDomainScores] = useState({});
  const [domainAttempts, setDomainAttempts] = useState({});
  const [averageScore, setAverageScore] = useState(0);
  const [evaluation, setEvaluation] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('bm'); // bm or en
  const [bmTone, setBmTone] = useState('casual+'); // casual+ or formal
  
  const currentDomain = DOMAINS[currentDomainIndex];
  const totalDomains = DOMAINS.length;
  const completedDomains = Object.keys(domainScores).length;
  const currentAttempts = domainAttempts[currentDomain?.name] || 0;

  // Calculate average score in real-time
  useEffect(() => {
    if (Object.keys(domainScores).length > 0) {
      const scores = Object.values(domainScores);
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      setAverageScore(Math.round(avg * 10) / 10);
    }
  }, [domainScores]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build context from Knowledge Packs
      const context = buildSOAContext(productId, campaignId);
      
      // Add language preferences
      context.language = language;
      context.bm_tone = bmTone;
      context.studio_type = studioType;
      
      const response = await startSOADrill({
        user_id: user?.user_id || 'demo_user',
        context: context
      });
      
      setSessionId(response.session_id);
      setCurrentQuestion(response.question);
      setCurrentDomainIndex(0);
      setDomainAttempts({ [currentDomain.name]: 1 });
      setStep('drilling');
    } catch (err) {
      setError(err.message || 'Gagal memulakan SOA Drill');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim()) {
      setError(language === 'bm' ? 'Sila berikan jawapan anda' : 'Please provide an answer');
      return;
    }
    
    if (answer.trim().split(' ').length < 10) {
      setError(language === 'bm' 
        ? 'Sila berikan jawapan yang lebih terperinci (sekurang-kurangnya 10 patah perkataan)'
        : 'Please provide a more detailed answer (at least 10 words)'
      );
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await submitSOAAnswer({
        session_id: sessionId,
        answer: answer
      });
      
      setEvaluation(response.evaluation);
      const score = response.evaluation.score / 10; // Convert to 0-10 scale
      
      // Adaptive Coaching Logic
      const needsRetry = score < 8 && currentAttempts < 3;
      
      if (needsRetry) {
        // Show evaluation and allow retry
        setTimeout(() => {
          setEvaluation(null);
          setAnswer('');
          setDomainAttempts(prev => ({
            ...prev,
            [currentDomain.name]: (prev[currentDomain.name] || 0) + 1
          }));
        }, 3000);
      } else {
        // Score ≥8 OR max attempts reached - move to next domain
        setDomainScores(prev => ({ ...prev, [currentDomain.name]: score }));
        
        setTimeout(() => {
          if (response.is_complete || currentDomainIndex >= totalDomains - 1) {
            handleCompleteDrill();
          } else {
            // Move to next domain
            setCurrentDomainIndex(prev => prev + 1);
            setCurrentQuestion(response.next_question?.question || '');
            setAnswer('');
            setEvaluation(null);
            setDomainAttempts(prev => ({
              ...prev,
              [DOMAINS[currentDomainIndex + 1].name]: 1
            }));
          }
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Gagal menghantar jawapan');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteDrill = async () => {
    setLoading(true);
    
    try {
      const response = await completeSOADrill(sessionId);
      setResults(response.results);
      setStep('results');
    } catch (err) {
      setError(err.message || 'Gagal menyiapkan drill');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToStudio = () => {
    if (onComplete) {
      onComplete({
        sessionId,
        averageScore,
        domainScores,
        bpEarned: results?.total_bp || 0,
        results
      });
    }
  };

  // Intro Screen
  const renderIntro = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <span className="text-2xl">🧩</span>
          </div>
          <div>
            <h1 className="text-lg font-bold">
              {language === 'bm' ? 'SOA Drill' : 'SOA Drill'}
            </h1>
            <p className="text-xs opacity-90">
              {language === 'bm' ? 'Penilaian Kesedaran Kreatif 5-Domain' : '5-Domain Creative Assessment'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        <div className="max-w-md mx-auto">
          {/* Language Toggle */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="text-xs font-medium text-gray-700 mb-2">
              {language === 'bm' ? 'Pilih Bahasa' : 'Select Language'}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setLanguage('bm')}
                className={`flex-1 h-10 rounded-lg font-medium text-sm ${
                  language === 'bm'
                    ? 'bg-[#0e67a2] text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                Bahasa Melayu
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`flex-1 h-10 rounded-lg font-medium text-sm ${
                  language === 'en'
                    ? 'bg-[#0e67a2] text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                English
              </button>
            </div>

            {language === 'bm' && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-700 mb-2">Nada BM</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setBmTone('casual+')}
                    className={`flex-1 h-9 rounded-lg font-medium text-xs ${
                      bmTone === 'casual+'
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Santai+
                  </button>
                  <button
                    onClick={() => setBmTone('formal')}
                    className={`flex-1 h-9 rounded-lg font-medium text-xs ${
                      bmTone === 'formal'
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Formal
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Domain Overview */}
          <div className="mb-4">
            <h2 className="text-base font-bold text-gray-800 mb-2">
              {language === 'bm' ? '5 Domain Penilaian' : '5 Assessment Domains'}
            </h2>
            <div className="space-y-2">
              {DOMAINS.map((domain) => (
                <div key={domain.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-2xl">{domain.icon}</span>
                  <span className="text-sm font-medium text-gray-800">
                    {language === 'bm' ? domain.label_bm : domain.label_en}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-xl">
            <p className="text-xs text-gray-700">
              {language === 'bm'
                ? '💡 Anda akan menjawab 5 soalan. Jika skor < 8/10, anda akan cuba semula (maksimum 3 percubaan per domain).'
                : '💡 You will answer 5 questions. If score < 8/10, you will retry (max 3 attempts per domain).'}
            </p>
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
              {error}
            </div>
          )}

          <button
            onClick={handleStart}
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base disabled:opacity-50 mb-2"
          >
            {loading 
              ? (language === 'bm' ? 'Memulakan...' : 'Starting...') 
              : (language === 'bm' ? 'Mula SOA Drill 🚀' : 'Start SOA Drill 🚀')}
          </button>

          {onSkip && (
            <button
              onClick={onSkip}
              className="w-full h-12 border border-gray-300 text-gray-700 font-medium rounded-xl text-sm"
            >
              {language === 'bm' ? 'Langkau (Guna AI Terus)' : 'Skip (Use AI Directly)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Drilling Screen
  const renderDrilling = () => {
    const needsRetry = evaluation && (evaluation.score / 10) < 8 && currentAttempts < 3;
    
    return (
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{currentDomain.icon}</span>
              <h1 className="text-base font-bold">
                {language === 'bm' ? currentDomain.label_bm : currentDomain.label_en}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                {completedDomains + 1}/{totalDomains}
              </span>
              <span className="text-xs bg-orange-500 px-2 py-1 rounded-full">
                {currentAttempts}/3
              </span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-white/20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((completedDomains + 1) / totalDomains) * 100}%` }}
            />
          </div>
        </div>

        {/* Real-time Scores */}
        {Object.keys(domainScores).length > 0 && (
          <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">
                {language === 'bm' ? 'Skor Purata' : 'Average Score'}
              </div>
              <div className="text-2xl font-black text-orange-600">
                {averageScore.toFixed(1)}/10
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              {Object.entries(domainScores).map(([domain, score]) => (
                <div key={domain} className="text-xs text-gray-600">
                  {DOMAINS.find(d => d.name === domain)?.icon} {score.toFixed(1)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-safe">
          <div className="max-w-md mx-auto">
            {/* Question */}
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm font-medium text-gray-800">{currentQuestion}</p>
            </div>

            {/* Evaluation */}
            {evaluation && (
              <div className={`mb-4 p-4 rounded-xl border ${
                needsRetry 
                  ? 'bg-yellow-50 border-yellow-300' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{needsRetry ? '⚠️' : '✅'}</span>
                  <span className="text-lg font-bold text-gray-800">
                    {language === 'bm' ? 'Skor' : 'Score'}: {(evaluation.score / 10).toFixed(1)}/10
                  </span>
                </div>
                <p className="text-xs text-gray-700 mb-2">{evaluation.feedback}</p>
                {needsRetry && (
                  <p className="text-xs font-bold text-orange-700">
                    {language === 'bm' 
                      ? `🔄 Cuba lagi (Percubaan ${currentAttempts}/3)`
                      : `🔄 Try again (Attempt ${currentAttempts}/3)`}
                  </p>
                )}
              </div>
            )}

            {/* Answer input */}
            {!evaluation && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'bm' ? 'Jawapan Anda' : 'Your Answer'}
                </label>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={language === 'bm' 
                    ? "Berikan jawapan yang terperinci (minimum 10 patah perkataan)"
                    : "Provide a detailed answer (minimum 10 words)"}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm resize-none mb-3"
                />

                <div className="text-xs text-gray-500 mb-3">
                  {language === 'bm' ? 'Kiraan perkataan' : 'Word count'}: {answer.trim().split(' ').filter(w => w).length}
                </div>

                {error && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSubmitAnswer}
                  disabled={loading}
                  className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base disabled:opacity-50"
                >
                  {loading 
                    ? (language === 'bm' ? 'Menilai...' : 'Evaluating...') 
                    : (language === 'bm' ? 'Hantar Jawapan' : 'Submit Answer')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Results Screen
  const renderResults = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
        <h1 className="text-lg font-bold">
          {language === 'bm' ? '🎉 Drill Selesai!' : '🎉 Drill Complete!'}
        </h1>
        <p className="text-xs opacity-90">
          {language === 'bm' ? 'Keputusan Penilaian SOA Anda' : 'Your SOA Assessment Results'}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        <div className="max-w-md mx-auto">
          {/* Overall Score */}
          <div className="mb-4 p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl text-center">
            <div className="text-5xl font-black text-[#0e67a2] mb-2">
              {averageScore.toFixed(1)}/10
            </div>
            <div className="text-sm text-gray-700 font-medium">
              {language === 'bm' ? 'Skor Keseluruhan' : 'Overall Score'}
            </div>
          </div>

          {/* BP Earned */}
          <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-300 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">
                  {language === 'bm' ? 'Brain Points Diperoleh' : 'Brain Points Earned'}
                </div>
                <div className="text-3xl font-black text-orange-600">+{results?.total_bp || 0} BP</div>
              </div>
              <div className="text-4xl">🧠</div>
            </div>
          </div>

          {/* Domain Breakdown */}
          <div className="mb-4">
            <h3 className="text-sm font-bold text-gray-800 mb-2">
              {language === 'bm' ? 'Pecahan Domain' : 'Domain Breakdown'}
            </h3>
            {DOMAINS.map((domain) => {
              const score = domainScores[domain.name] || 0;
              return (
                <div key={domain.name} className="mb-2">
                  <div className="flex justify-between text-xs text-gray-700 mb-1">
                    <span>{domain.icon} {language === 'bm' ? domain.label_bm : domain.label_en}</span>
                    <span className="font-bold">{score.toFixed(1)}/10</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#0e67a2] h-2 rounded-full"
                      style={{ width: `${(score / 10) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nexus Academy Prompt */}
          {results?.weakest_domain && (
            <div className="mb-4 p-4 bg-purple-50 border border-purple-300 rounded-xl">
              <h3 className="text-sm font-bold text-gray-800 mb-1">
                🎓 {language === 'bm' ? 'Pembelajaran Disyorkan' : 'Recommended Learning'}
              </h3>
              <p className="text-xs text-gray-600 mb-2">
                {language === 'bm'
                  ? `Perkukuhkan kemahiran ${DOMAINS.find(d => d.name === results.weakest_domain)?.label_bm} anda:`
                  : `Strengthen your ${results.weakest_domain} skills:`}
              </p>
              <button
                onClick={() => {/* TODO: Navigate to Nexus Academy */}}
                className="w-full h-10 bg-purple-600 text-white font-medium rounded-lg text-xs"
              >
                {language === 'bm' ? 'Ketahui Lebih Lanjut di Nexus Academy' : 'Learn More in Nexus Academy'}
              </button>
            </div>
          )}

          <button
            onClick={handleContinueToStudio}
            className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base"
          >
            {language === 'bm' ? 'Teruskan ke Studio' : 'Continue to Studio'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-dvh w-full overflow-hidden bg-white">
      {step === 'intro' && renderIntro()}
      {step === 'drilling' && renderDrilling()}
      {step === 'results' && renderResults()}
    </div>
  );
};

export default SOADrillInStudio;
